import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Headset, Send, X, Clock, MessageCircle, CheckCircle2, PlayCircle, BadgeCheck, Search, Lock, ShieldCheck, Star, AlertTriangle, Paperclip } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { brl, fmtDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";

const STATUS_LABEL = { aberta: "Aguardando", aceita: "Aceito", em_andamento: "Em atendimento", concluida: "Concluído", cancelada: "Cancelado", recusada: "Recusado" };
const STATUS_COLOR = {
  aberta: "bg-amber-100 text-amber-700 border-amber-200",
  aceita: "bg-sky-100 text-sky-700 border-sky-200",
  em_andamento: "bg-blue-100 text-blue-700 border-blue-200",
  concluida: "bg-green-100 text-green-700 border-green-200",
  cancelada: "bg-slate-100 text-slate-600 border-slate-200",
  recusada: "bg-red-100 text-red-700 border-red-200",
};
const PAY_LABEL = {
  checkout_criado: "Aguardando pagamento",
  pago_retido: "Em custódia GEPR — liberado ao especialista após a conclusão",
  liberacao_pendente: "Sessão concluída — repasse ao especialista em processamento",
  liberado: "Pago ao especialista",
  reembolso_pendente: "Reembolso em processamento",
  reembolsado: "Reembolsado ao pagador",
  cancelado: "Pagamento cancelado",
};
const REVIEW_TAGS = [
  { id: "resolveu", name: "Resolveu o problema" },
  { id: "didatico", name: "Didático" },
  { id: "rapido", name: "Respondeu rápido" },
  { id: "atencioso", name: "Atencioso" },
  { id: "pontual", name: "Pontual" },
];
const DISPUTE_REASONS = [
  { id: "servico_nao_prestado", name: "Serviço não foi prestado" },
  { id: "servico_incompleto", name: "Serviço incompleto ou aquém do combinado" },
  { id: "problema_pagamento", name: "Problema com o pagamento" },
  { id: "conduta", name: "Conduta inadequada" },
  { id: "outro", name: "Outro motivo" },
];
const toggleList = (list, v) => (list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);

export default function Assist() {
  const { user } = useAuth();
  const myId = user?.id || user?._id;
  const [specialists, setSpecialists] = useState([]);
  const [meta, setMeta] = useState({ specialties: [], tools: [] });
  const [requests, setRequests] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [profile, setProfile] = useState(null);
  const [search, setSearch] = useState("");
  const [specFilter, setSpecFilter] = useState("");
  const [requestFor, setRequestFor] = useState(null);
  const [reqForm, setReqForm] = useState({ title: "", description: "", equipment: "", urgency: "normal" });
  const [sending, setSending] = useState(false);
  const [chatReq, setChatReq] = useState(null);
  const [messages, setMessages] = useState([]);
  const [chatText, setChatText] = useState("");
  const chatEndRef = useRef(null);
  const [pform, setPform] = useState({ specialties: [], description: "", price_session: "", session_minutes: 30, tools: [], response_time: "", pix_key: "", active: true });
  const [reviewFor, setReviewFor] = useState(null);
  const [reviewForm, setReviewForm] = useState({ rating: 0, comment: "", tags: [] });
  const [reviewsFor, setReviewsFor] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [disputeFor, setDisputeFor] = useState(null);
  const [disputeForm, setDisputeForm] = useState({ reason: "", description: "", evidence: [] });

  const load = () => {
    api.get("/assist/specialties").then((r) => setMeta(r.data)).catch(() => {});
    api.get("/assist/requests", { params: { role: "requester" } }).then((r) => setRequests(r.data)).catch(() => {});
    api.get("/assist/requests", { params: { role: "specialist" } }).then((r) => setJobs(r.data)).catch(() => {});
    api.get("/assist/profile/me").then((r) => setProfile(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const p = searchParams.get("pago");
    if (p === "sucesso") toast.success("Pagamento recebido! O valor fica em custódia GEPR até a conclusão da sessão.");
    else if (p === "pendente") toast.info("Pagamento pendente de confirmação pelo Mercado Pago.");
    else if (p === "falha") toast.error("Pagamento não concluído. Tente novamente.");
    if (p) setSearchParams({}, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    api.get("/assist/specialists", { params: { specialty: specFilter, q: search } }).then((r) => setSpecialists(r.data)).catch(() => {});
  }, [specFilter, search]);

  useEffect(() => {
    if (profile?.exists) {
      setPform({
        specialties: profile.specialties || [], description: profile.description || "",
        price_session: profile.price_session ?? "", session_minutes: profile.session_minutes || 30,
        tools: profile.tools || [], response_time: profile.response_time || "",
        pix_key: profile.pix_key || "", active: profile.active !== false,
      });
    }
  }, [profile]);

  useEffect(() => {
    if (!chatReq) return;
    let alive = true;
    const fetchMsgs = () => api.get(`/assist/requests/${chatReq.id}/messages`).then((r) => { if (alive) setMessages(r.data); }).catch(() => {});
    fetchMsgs();
    const t = setInterval(fetchMsgs, 5000);
    return () => { alive = false; clearInterval(t); };
  }, [chatReq]);

  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const specName = (id) => meta.specialties.find((s) => s.id === id)?.name || id;
  const toolName = (id) => meta.tools.find((t) => t.id === id)?.name || id;

  const createRequest = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data } = await api.post("/assist/requests", { ...reqForm, specialist_id: requestFor.id });
      toast.success(data.message);
      setRequestFor(null);
      setReqForm({ title: "", description: "", equipment: "", urgency: "normal" });
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSending(false);
    }
  };

  const updateStatus = async (rid, status) => {
    try {
      await api.put(`/assist/requests/${rid}/status`, { status });
      toast.success("Status atualizado.");
      if (chatReq?.id === rid && ["concluida", "cancelada", "recusada"].includes(status)) setChatReq(null);
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!chatText.trim()) return;
    try {
      const { data: sent } = await api.post(`/assist/requests/${chatReq.id}/messages`, { text: chatText });
      if (sent?.masked) toast.warning("Telefone ou e-mail foram ocultados: negociar fora da plataforma fere os Termos GEPR e pode suspender sua conta.");
      setChatText("");
      const { data } = await api.get(`/assist/requests/${chatReq.id}/messages`);
      setMessages(data);
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const payRequest = async (r) => {
    try {
      const { data } = await api.post(`/assist/requests/${r.id}/checkout`);
      if (data.init_point) window.location.assign(data.init_point);
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const releasePayment = async (r) => {
    try {
      await api.post(`/assist/payments/${r.payment_id}/release`);
      toast.success("Pagamento liberado! O repasse ao especialista será processado.");
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const loadReviews = async (s) => {
    if (reviewsFor === s.id) { setReviewsFor(null); return; }
    try {
      const { data } = await api.get(`/assist/specialists/${s.id}/reviews`);
      setReviews(data);
      setReviewsFor(s.id);
    } catch {
      toast.error("Erro ao carregar avaliações.");
    }
  };

  const submitReview = async (e) => {
    e.preventDefault();
    if (!reviewForm.rating) { toast.error("Selecione de 1 a 5 estrelas."); return; }
    setSending(true);
    try {
      await api.post(`/assist/requests/${reviewFor.id}/review`, reviewForm);
      toast.success("Avaliação enviada. Obrigado!");
      setReviewFor(null);
      setReviewForm({ rating: 0, comment: "", tags: [] });
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSending(false);
    }
  };

  const uploadEvidence = async (file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error("Arquivo maior que 10 MB."); return; }
    const fd = new FormData();
    fd.append("file", file);
    fd.append("context", "dispute");
    try {
      const { data } = await api.post("/files/upload", fd);
      setDisputeForm((f) => ({ ...f, evidence: [...f.evidence, { name: data.name, url: data.url }] }));
      toast.success("Evidência anexada.");
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const submitDispute = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data } = await api.post(`/assist/payments/${disputeFor.payment_id}/dispute`, { reason: disputeForm.reason, description: disputeForm.description, evidence: disputeForm.evidence });
      toast.success(data.message);
      setDisputeFor(null);
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSending(false);
    }
  };

  const saveProfile = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data } = await api.post("/assist/profile", { ...pform, price_session: Number(pform.price_session) || 0, session_minutes: Number(pform.session_minutes) || 30 });
      toast.success(data.message);
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSending(false);
    }
  };

  const chatAllowed = chatReq && ["aceita", "em_andamento"].includes(chatReq.status);

  return (
    <div className="space-y-5 animate-in-up max-w-3xl" data-testid="assist-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
          <Headset className="h-6 w-6 text-sky-600" /> GEPR Assist <Badge className="bg-amber-100 text-amber-700 border-amber-200">BETA</Badge>
        </h1>
        <p className="text-sm text-slate-500">Suporte técnico remoto entre profissionais — tire dúvidas com quem é especialista</p>
      </div>

      <Tabs defaultValue="encontrar">
        <TabsList className="w-full justify-start overflow-x-auto">
          <TabsTrigger value="encontrar" data-testid="assist-tab-find">Encontrar ajuda</TabsTrigger>
          <TabsTrigger value="pedidos" data-testid="assist-tab-requests">Meus pedidos ({requests.length})</TabsTrigger>
          <TabsTrigger value="atendimentos" data-testid="assist-tab-jobs">Atendimentos ({jobs.filter((j) => ["aberta", "aceita", "em_andamento"].includes(j.status)).length})</TabsTrigger>
          <TabsTrigger value="perfil" data-testid="assist-tab-profile">Perfil de especialista</TabsTrigger>
        </TabsList>

        <TabsContent value="encontrar" className="mt-4 space-y-3">
          {requestFor && (
            <Card className="border-sky-300 bg-sky-50" data-testid="request-form-card">
              <CardHeader>
                <CardTitle className="text-base">Pedir ajuda para {requestFor.name}</CardTitle>
                <p className="text-xs text-slate-500">
                  {brl(requestFor.price_session)} por sessão de ~{requestFor.session_minutes} min · Pagamento com garantia GEPR: o valor fica em custódia e só é repassado após a conclusão.
                </p>
              </CardHeader>
              <CardContent>
                <form onSubmit={createRequest} className="space-y-3">
                  <div className="space-y-1.5">
                    <Label>Título do problema *</Label>
                    <Input className="h-12 bg-white" data-testid="ar-title" required value={reqForm.title} onChange={(e) => setReqForm({ ...reqForm, title: e.target.value })} placeholder="Ex: Split inverter não gela, erro E4" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Descreva o problema em detalhes *</Label>
                    <Textarea rows={3} className="bg-white" data-testid="ar-description" required value={reqForm.description} onChange={(e) => setReqForm({ ...reqForm, description: e.target.value })} placeholder="O que já testou, medições feitas, sintomas..." />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label>Equipamento (marca/modelo)</Label>
                      <Input className="h-12 bg-white" data-testid="ar-equipment" value={reqForm.equipment} onChange={(e) => setReqForm({ ...reqForm, equipment: e.target.value })} />
                    </div>
                    <div className="space-y-1.5">
                      <Label>Urgência</Label>
                      <Select value={reqForm.urgency} onValueChange={(v) => setReqForm({ ...reqForm, urgency: v })}>
                        <SelectTrigger className="h-12 bg-white" data-testid="ar-urgency"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="normal">Normal</SelectItem>
                          <SelectItem value="urgente">Urgente</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button type="submit" className="flex-1 h-12 bg-sky-600 hover:bg-sky-700 text-white" disabled={sending} data-testid="ar-submit">
                      <Send className="h-4 w-4 mr-2" /> {sending ? "Enviando..." : "Enviar pedido"}
                    </Button>
                    <Button type="button" variant="outline" className="h-12" onClick={() => setRequestFor(null)} data-testid="ar-cancel">Cancelar</Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          )}

          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="h-4 w-4 absolute left-3 top-3.5 text-slate-400" />
              <Input className="h-12 pl-9" placeholder="Buscar por nome..." data-testid="assist-search" value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
            <Select value={specFilter} onValueChange={setSpecFilter}>
              <SelectTrigger className="h-12 w-44" data-testid="assist-specialty-filter"><SelectValue placeholder="Especialidade" /></SelectTrigger>
              <SelectContent>
                {meta.specialties.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {specialists.length === 0 && (
            <Card className="border-slate-200"><CardContent className="p-6 text-center text-sm text-slate-500" data-testid="no-specialists">
              Nenhum especialista disponível no momento. Que tal ser o primeiro? Cadastre-se na aba <b>Perfil de especialista</b>.
            </CardContent></Card>
          )}
          {specialists.map((s) => (
            <Card key={s.id} className="border-slate-200" data-testid={`specialist-card-${s.id}`}>
              <CardContent className="p-4 space-y-2.5">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-semibold text-sm flex items-center gap-1.5">
                      {s.name} {s.verified && <BadgeCheck className="h-4 w-4 text-sky-600" data-testid={`verified-${s.id}`} />}
                    </p>
                    <p className="text-xs text-slate-500">{[s.city, s.state].filter(Boolean).join("/") || "Atendimento remoto"} · {s.sessions_completed} atendimentos{s.acceptance != null && ` · ${s.acceptance}% de aceite`}</p>
                    <p className={`text-xs flex items-center gap-1 mt-0.5 ${s.rating_count > 0 ? "cursor-pointer hover:underline" : ""}`} onClick={() => s.rating_count > 0 && loadReviews(s)} data-testid={`rating-${s.id}`}>
                      {s.rating_count > 0 ? (
                        <><Star className="h-3.5 w-3.5 text-amber-400 fill-amber-400" /><span className="font-mono-num font-semibold">{s.rating_avg.toFixed(1)}</span><span className="text-slate-400">({s.rating_count} {s.rating_count === 1 ? "avaliação" : "avaliações"})</span></>
                      ) : (
                        <span className="text-slate-400">Sem avaliações ainda</span>
                      )}
                    </p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="font-mono-num font-semibold text-sky-700">{s.price_session > 0 ? brl(s.price_session) : "A combinar"}</p>
                    <p className="text-[11px] text-slate-500">~{s.session_minutes} min</p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {s.specialties.map((sp) => <Badge key={sp} variant="outline" className="text-[10px] bg-sky-50 text-sky-700 border-sky-200">{specName(sp)}</Badge>)}
                </div>
                <p className="text-sm text-slate-600 line-clamp-2">{s.description}</p>
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <p className="text-[11px] text-slate-500 flex items-center gap-1">
                    <Clock className="h-3 w-3" /> {s.response_time || "Resposta a combinar"} · {s.tools.map(toolName).join(" · ")}
                  </p>
                  {!s.is_me && (
                    <Button size="sm" className="h-10 bg-sky-600 hover:bg-sky-700 text-white" onClick={() => setRequestFor(s)} data-testid={`specialist-request-btn-${s.id}`}>
                      Pedir ajuda
                    </Button>
                  )}
                  {s.is_me && <Badge variant="outline" className="text-[10px]">Você</Badge>}
                </div>
                {reviewsFor === s.id && (
                  <div className="border-t border-slate-100 pt-2 space-y-2" data-testid={`reviews-${s.id}`}>
                    {reviews.length === 0 && <p className="text-xs text-slate-500">Sem avaliações.</p>}
                    {reviews.map((rv) => (
                      <div key={rv.id} className="text-xs bg-slate-50 rounded-lg p-2.5">
                        <p className="flex items-center gap-1 font-medium"><Star className="h-3 w-3 text-amber-400 fill-amber-400" /> {rv.rating} · {rv.reviewer} · {fmtDate(rv.created_at)}</p>
                        {rv.comment && <p className="text-slate-600 mt-0.5">{rv.comment}</p>}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        <TabsContent value="pedidos" className="mt-4 space-y-2">
          {requests.length === 0 && <p className="text-sm text-slate-500">Você ainda não pediu ajuda. Encontre um especialista na primeira aba.</p>}
          {requests.map((r) => (
            <Card key={r.id} className="border-slate-200" data-testid={`request-card-${r.id}`}>
              <CardContent className="p-4 space-y-2">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-medium text-sm">{r.title} {r.urgency === "urgente" && <Badge className="ml-1 bg-red-100 text-red-700 border-red-200 text-[10px]">URGENTE</Badge>}</div>
                    <p className="text-xs text-slate-500">com {r.specialist_name} · {fmtDate(r.created_at)}{r.equipment ? ` · ${r.equipment}` : ""}</p>
                  </div>
                  <Badge variant="outline" className={`text-[10px] shrink-0 ${STATUS_COLOR[r.status]}`}>{STATUS_LABEL[r.status]}</Badge>
                </div>
                <p className="text-sm text-slate-600 line-clamp-2">{r.description}</p>
                {r.payment_status && (
                  <p className="text-xs flex items-center gap-1.5 text-slate-600 bg-slate-50 border border-slate-200 rounded-lg p-2" data-testid={`payment-badge-${r.id}`}>
                    <Lock className="h-3.5 w-3.5 text-sky-600 shrink-0" /> {PAY_LABEL[r.payment_status] || r.payment_status}
                  </p>
                )}
                {r.payment_dispute && (
                  <p className="text-xs flex items-center gap-1.5 text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-2" data-testid={`dispute-badge-${r.id}`}>
                    <AlertTriangle className="h-3.5 w-3.5 shrink-0" /> Em disputa — a equipe GEPR está mediando este pagamento.
                  </p>
                )}
                <div className="flex gap-2 flex-wrap">
                  {["aceita", "em_andamento"].includes(r.status) && (
                    <Button size="sm" variant="outline" className="h-10" onClick={() => setChatReq(r)} data-testid={`request-open-${r.id}`}>
                      <MessageCircle className="h-4 w-4 mr-1" /> Abrir chat
                    </Button>
                  )}
                  {["aceita", "em_andamento"].includes(r.status) && r.price_session > 0 && (!r.payment_status || r.payment_status === "cancelado") && (
                    <Button size="sm" className="h-10 bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => payRequest(r)} data-testid={`request-pay-${r.id}`}>
                      <ShieldCheck className="h-4 w-4 mr-1" /> Pagar {brl(r.price_session)} com garantia
                    </Button>
                  )}
                  {r.payment_status === "pago_retido" && r.status === "em_andamento" && !r.payment_dispute && (
                    <Button size="sm" className="h-10 bg-green-600 hover:bg-green-700 text-white" onClick={() => releasePayment(r)} data-testid={`request-release-${r.id}`}>
                      <CheckCircle2 className="h-4 w-4 mr-1" /> Liberar pagamento
                    </Button>
                  )}
                  {r.payment_status === "pago_retido" && !r.payment_dispute && (
                    <Button size="sm" variant="ghost" className="h-10 text-amber-700 hover:bg-amber-50" onClick={() => { setDisputeFor({ payment_id: r.payment_id, title: r.title }); setDisputeForm({ reason: "", description: "", evidence: [] }); }} data-testid={`dispute-open-${r.id}`}>
                      <AlertTriangle className="h-4 w-4 mr-1" /> Abrir disputa
                    </Button>
                  )}
                  {["aberta", "aceita"].includes(r.status) && r.payment_status !== "pago_retido" && (
                    <Button size="sm" variant="ghost" className="h-10 text-red-600 hover:bg-red-50" onClick={() => updateStatus(r.id, "cancelada")} data-testid={`request-cancel-${r.id}`}>Cancelar</Button>
                  )}
                  {r.status === "em_andamento" && (
                    <Button size="sm" className="h-10 bg-green-600 hover:bg-green-700 text-white" onClick={() => updateStatus(r.id, "concluida")} data-testid={`request-complete-${r.id}`}>
                      <CheckCircle2 className="h-4 w-4 mr-1" /> Concluir sessão
                    </Button>
                  )}
                  {r.status === "concluida" && !r.has_review && (
                    <Button size="sm" variant="outline" className="h-10 border-amber-300 text-amber-700 hover:bg-amber-50" onClick={() => { setReviewFor({ id: r.id, name: r.specialist_name }); setReviewForm({ rating: 0, comment: "", tags: [] }); }} data-testid={`review-open-${r.id}`}>
                      <Star className="h-4 w-4 mr-1" /> Avaliar atendimento
                    </Button>
                  )}
                  {r.status === "concluida" && r.has_review && (
                    <p className="text-xs text-green-700 flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5" /> Avaliação enviada</p>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        <TabsContent value="atendimentos" className="mt-4 space-y-2">
          {jobs.length === 0 && <p className="text-sm text-slate-500">Nenhum pedido recebido. Ative seu perfil de especialista para aparecer na busca.</p>}
          {jobs.map((r) => (
            <Card key={r.id} className="border-slate-200" data-testid={`job-card-${r.id}`}>
              <CardContent className="p-4 space-y-2">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-medium text-sm">{r.title} {r.urgency === "urgente" && <Badge className="ml-1 bg-red-100 text-red-700 border-red-200 text-[10px]">URGENTE</Badge>}</div>
                    <p className="text-xs text-slate-500">de {r.requester_name} · {fmtDate(r.created_at)}{r.equipment ? ` · ${r.equipment}` : ""} · {r.price_session > 0 ? brl(r.price_session) : "a combinar"}</p>
                  </div>
                  <Badge variant="outline" className={`text-[10px] shrink-0 ${STATUS_COLOR[r.status]}`}>{STATUS_LABEL[r.status]}</Badge>
                </div>
                <p className="text-sm text-slate-600">{r.description}</p>
                {r.payment_status && (
                  <p className="text-xs flex items-center gap-1.5 text-slate-600 bg-slate-50 border border-slate-200 rounded-lg p-2" data-testid={`job-payment-badge-${r.id}`}>
                    <Lock className="h-3.5 w-3.5 text-sky-600 shrink-0" /> {PAY_LABEL[r.payment_status] || r.payment_status}
                  </p>
                )}
                <div className="flex gap-2 flex-wrap">
                  {r.status === "aberta" && (
                    <>
                      <Button size="sm" className="h-10 bg-sky-600 hover:bg-sky-700 text-white" onClick={() => updateStatus(r.id, "aceita")} data-testid={`job-accept-${r.id}`}>Aceitar</Button>
                      <Button size="sm" variant="ghost" className="h-10 text-red-600 hover:bg-red-50" onClick={() => updateStatus(r.id, "recusada")} data-testid={`job-decline-${r.id}`}>Recusar</Button>
                    </>
                  )}
                  {r.status === "aceita" && (
                    <Button size="sm" className="h-10 bg-blue-600 hover:bg-blue-700 text-white" onClick={() => updateStatus(r.id, "em_andamento")} data-testid={`job-start-${r.id}`}>
                      <PlayCircle className="h-4 w-4 mr-1" /> Iniciar atendimento
                    </Button>
                  )}
                  {["aceita", "em_andamento"].includes(r.status) && (
                    <Button size="sm" variant="outline" className="h-10" onClick={() => setChatReq(r)} data-testid={`job-open-${r.id}`}>
                      <MessageCircle className="h-4 w-4 mr-1" /> Abrir chat
                    </Button>
                  )}
                  {r.status === "em_andamento" && (
                    <Button size="sm" className="h-10 bg-green-600 hover:bg-green-700 text-white" onClick={() => updateStatus(r.id, "concluida")} data-testid={`job-complete-${r.id}`}>
                      <CheckCircle2 className="h-4 w-4 mr-1" /> Concluir sessão
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        <TabsContent value="perfil" className="mt-4">
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="text-base">{profile?.exists ? "Meu perfil de especialista" : "Torne-se um especialista GEPR Assist"}</CardTitle>
              <p className="text-xs text-slate-500">Ajude outros profissionais por videochamada, fotos ou chat — e receba via Pix com o pagamento seguro GEPR (valor em custódia até a conclusão).</p>
            </CardHeader>
            <CardContent>
              <form onSubmit={saveProfile} className="space-y-4">
                {profile?.exists && (
                  <div className="flex flex-wrap items-center gap-4 border border-slate-200 rounded-lg p-3 text-sm" data-testid="my-specialist-stats">
                    <span className="flex items-center gap-1"><Star className="h-4 w-4 text-amber-400 fill-amber-400" /><b className="font-mono-num">{(profile.rating_avg || 0).toFixed(1)}</b> <span className="text-slate-500">({profile.rating_count || 0})</span></span>
                    <span><b className="font-mono-num">{profile.sessions_completed || 0}</b> <span className="text-slate-500">atendimentos</span></span>
                    {profile.verified
                      ? <Badge className="bg-sky-100 text-sky-700 border-sky-200 text-[10px]">Especialista verificado</Badge>
                      : <span className="text-slate-500 text-xs">Verificação pela equipe GEPR pendente</span>}
                  </div>
                )}
                <div className="flex items-center justify-between border border-slate-200 rounded-lg p-3">
                  <div>
                    <p className="font-medium text-sm">Perfil ativo na busca</p>
                    <p className="text-xs text-slate-500">Desative para pausar novos pedidos</p>
                  </div>
                  <Switch checked={pform.active} onCheckedChange={(v) => setPform({ ...pform, active: v })} data-testid="ap-active" />
                </div>
                <div className="space-y-1.5">
                  <Label>Especialidades *</Label>
                  <div className="grid grid-cols-2 gap-2">
                    {meta.specialties.map((s) => (
                      <label key={s.id} className="flex items-center gap-2 border border-slate-200 rounded-lg p-2.5 cursor-pointer text-sm" data-testid={`ap-spec-${s.id}`}>
                        <Checkbox checked={pform.specialties.includes(s.id)} onCheckedChange={() => setPform({ ...pform, specialties: toggleList(pform.specialties, s.id) })} />
                        {s.name}
                      </label>
                    ))}
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label>Sua experiência (mín. 20 caracteres) *</Label>
                  <Textarea rows={3} data-testid="ap-description" value={pform.description} onChange={(e) => setPform({ ...pform, description: e.target.value })} placeholder="Ex: 15 anos com câmaras frias, diagnóstico de falhas em controladores..." />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label>Preço por sessão (R$)</Label>
                    <Input className="h-12 font-mono-num" type="number" step="0.01" min="0" data-testid="ap-price" value={pform.price_session} onChange={(e) => setPform({ ...pform, price_session: e.target.value })} placeholder="0 = a combinar" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Duração (min)</Label>
                    <Input className="h-12 font-mono-num" type="number" min="15" step="5" data-testid="ap-minutes" value={pform.session_minutes} onChange={(e) => setPform({ ...pform, session_minutes: e.target.value })} />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label>Formas de atendimento</Label>
                  <div className="space-y-2">
                    {meta.tools.map((t) => (
                      <label key={t.id} className="flex items-center gap-2 border border-slate-200 rounded-lg p-2.5 cursor-pointer text-sm" data-testid={`ap-tool-${t.id}`}>
                        <Checkbox checked={pform.tools.includes(t.id)} onCheckedChange={() => setPform({ ...pform, tools: toggleList(pform.tools, t.id) })} />
                        {t.name}
                      </label>
                    ))}
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label>Tempo médio de resposta</Label>
                  <Select value={pform.response_time} onValueChange={(v) => setPform({ ...pform, response_time: v })}>
                    <SelectTrigger className="h-12" data-testid="ap-response"><SelectValue placeholder="Selecione" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="em até 1h">em até 1h</SelectItem>
                      <SelectItem value="em até 4h">em até 4h</SelectItem>
                      <SelectItem value="em até 24h">em até 24h</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Chave Pix (para receber pelos atendimentos)</Label>
                  <Input className="h-12" data-testid="ap-pix" value={pform.pix_key} onChange={(e) => setPform({ ...pform, pix_key: e.target.value })} placeholder="CPF, e-mail, telefone ou chave aleatória" />
                  <p className="text-[11px] text-slate-500">Visível apenas para a administração, usada no repasse dos seus atendimentos.</p>
                </div>
                <Button type="submit" className="w-full h-12 bg-sky-600 hover:bg-sky-700 text-white" disabled={sending} data-testid="ap-save">
                  {sending ? "Salvando..." : "Salvar perfil de especialista"}
                </Button>
              </form>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {disputeFor && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center" data-testid="dispute-modal">
          <div className="bg-white w-full sm:max-w-md sm:rounded-xl rounded-t-xl p-5 space-y-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between">
              <p className="font-semibold">Abrir disputa</p>
              <Button variant="ghost" size="icon" className="h-10 w-10" onClick={() => setDisputeFor(null)} data-testid="dispute-close"><X className="h-5 w-5" /></Button>
            </div>
            <p className="text-xs text-slate-500">O valor de <b>{disputeFor.title}</b> segue em custódia até a mediação da equipe GEPR.</p>
            <form onSubmit={submitDispute} className="space-y-3">
              <div className="space-y-1.5">
                <Label>Motivo *</Label>
                <Select value={disputeForm.reason} onValueChange={(v) => setDisputeForm({ ...disputeForm, reason: v })}>
                  <SelectTrigger className="h-12" data-testid="dispute-reason"><SelectValue placeholder="Selecione" /></SelectTrigger>
                  <SelectContent>
                    {DISPUTE_REASONS.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Descreva o ocorrido * (mín. 20 caracteres)</Label>
                <Textarea rows={4} data-testid="dispute-description" value={disputeForm.description} onChange={(e) => setDisputeForm({ ...disputeForm, description: e.target.value })} maxLength={1000} />
              </div>
              <div className="space-y-1.5">
                <Label>Evidências (fotos, prints ou PDF — até 3, máx. 10 MB cada)</Label>
                {disputeForm.evidence.map((ev, i) => (
                  <p key={i} className="text-xs text-green-800 bg-green-50 border border-green-200 rounded-lg p-2 flex items-center justify-between" data-testid={`dispute-file-${i}`}>
                    <span className="truncate">{ev.name}</span>
                    <button type="button" className="text-red-500 ml-2 shrink-0" onClick={() => setDisputeForm({ ...disputeForm, evidence: disputeForm.evidence.filter((_, xi) => xi !== i) })}>remover</button>
                  </p>
                ))}
                {disputeForm.evidence.length < 3 && (
                  <label className="flex items-center justify-center gap-2 border-2 border-dashed border-slate-300 rounded-lg p-3 cursor-pointer text-sm text-slate-600 hover:border-sky-400 transition-colors" data-testid="dispute-upload">
                    <Paperclip className="h-4 w-4" /> Anexar arquivo
                    <input type="file" className="hidden" accept="image/*,application/pdf" onChange={(e) => { uploadEvidence(e.target.files[0]); e.target.value = ""; }} />
                  </label>
                )}
              </div>
              <Button type="submit" className="w-full h-12 bg-red-600 hover:bg-red-700 text-white" disabled={sending || !disputeForm.reason} data-testid="dispute-submit">{sending ? "Enviando..." : "Abrir disputa"}</Button>
            </form>
          </div>
        </div>
      )}

      {reviewFor && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center" data-testid="review-modal">
          <div className="bg-white w-full sm:max-w-md sm:rounded-xl rounded-t-xl p-5 space-y-4">
            <div className="flex items-center justify-between">
              <p className="font-semibold">Avaliar {reviewFor.name}</p>
              <Button variant="ghost" size="icon" className="h-10 w-10" onClick={() => setReviewFor(null)} data-testid="review-close"><X className="h-5 w-5" /></Button>
            </div>
            <form onSubmit={submitReview} className="space-y-4">
              <div className="flex justify-center gap-1.5">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button key={n} type="button" onClick={() => setReviewForm({ ...reviewForm, rating: n })} data-testid={`review-star-${n}`} className="p-1.5">
                    <Star className={`h-8 w-8 ${n <= reviewForm.rating ? "text-amber-400 fill-amber-400" : "text-slate-300"}`} />
                  </button>
                ))}
              </div>
              <div className="flex flex-wrap gap-2 justify-center">
                {REVIEW_TAGS.map((t) => (
                  <button key={t.id} type="button" onClick={() => setReviewForm({ ...reviewForm, tags: toggleList(reviewForm.tags, t.id) })} data-testid={`review-tag-${t.id}`}
                    className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${reviewForm.tags.includes(t.id) ? "bg-sky-600 text-white border-sky-600" : "border-slate-300 text-slate-600"}`}>
                    {t.name}
                  </button>
                ))}
              </div>
              <Textarea rows={3} placeholder="Como foi o atendimento? (opcional)" data-testid="review-comment" value={reviewForm.comment} onChange={(e) => setReviewForm({ ...reviewForm, comment: e.target.value })} maxLength={500} />
              <Button type="submit" className="w-full h-12 bg-sky-600 hover:bg-sky-700 text-white" disabled={sending} data-testid="review-submit">{sending ? "Enviando..." : "Enviar avaliação"}</Button>
            </form>
          </div>
        </div>
      )}

      {chatReq && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center" data-testid="chat-dialog">
          <div className="bg-white w-full sm:max-w-lg sm:rounded-xl rounded-t-xl flex flex-col h-[85vh] sm:h-[70vh]">
            <div className="flex items-center justify-between p-4 border-b border-slate-200">
              <div className="min-w-0">
                <p className="font-semibold text-sm truncate">{chatReq.title}</p>
                <p className="text-xs text-slate-500">{chatReq.requester_id === myId ? `com ${chatReq.specialist_name}` : `com ${chatReq.requester_name}`} · {STATUS_LABEL[chatReq.status]}</p>
              </div>
              <Button variant="ghost" size="icon" className="h-10 w-10" onClick={() => setChatReq(null)} data-testid="chat-close"><X className="h-5 w-5" /></Button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-2" data-testid="chat-messages">
              {!chatAllowed && <p className="text-xs text-center text-slate-500 bg-slate-50 rounded-lg p-3">O chat fica disponível quando o especialista aceita o pedido.</p>}
              {messages.map((m) => (
                <div key={m.id} className={`flex ${m.sender_id === myId ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[80%] rounded-2xl px-3.5 py-2 text-sm ${m.sender_id === myId ? "bg-sky-600 text-white" : "bg-slate-100 text-slate-800"}`}>
                    {m.text}
                    <p className={`text-[10px] mt-0.5 ${m.sender_id === myId ? "text-sky-200" : "text-slate-400"}`}>{new Date(m.created_at).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}</p>
                  </div>
                </div>
              ))}
              <div ref={chatEndRef} />
            </div>
            {chatAllowed && (
              <form onSubmit={sendMessage} className="p-3 border-t border-slate-200 flex gap-2">
                <Input className="h-12 flex-1" placeholder="Escreva sua mensagem..." data-testid="chat-input" value={chatText} onChange={(e) => setChatText(e.target.value)} maxLength={1000} />
                <Button type="submit" size="icon" className="h-12 w-12 bg-sky-600 hover:bg-sky-700 text-white" data-testid="chat-send"><Send className="h-4 w-4" /></Button>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
